import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Permission } from 'src/app/enum/permission.enum';
import { PERMISSIONS_BY_TID } from 'src/app/constants/menu-config';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private permissions: Permission[] = [];
  private permissionsSubject = new BehaviorSubject<Permission[]>([]);
  private isPermissionsLoaded = new BehaviorSubject<boolean>(false);

  constructor(private authService: AuthService) {
    this.authService
      .onAuthInitialized()
      .pipe(
        switchMap((isInitialized) => {
          if (isInitialized) {
            return this.authService.getTenantId();
          }
          return of(null);
        }),
        tap((tid) => {
          if (tid) {
            this.loadPermissions(tid.toLowerCase());
          } else {
            console.warn('⚠️ No TID encontrado – reseteando permisos');
            this.resetPermissions();
          }
        }),
        catchError((error) => {
          console.error('❌ Error al cargar permisos:', error);
          this.resetPermissions();
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * ✅ Carga permisos desde la configuración por TID
   */
  private loadPermissions(tid: string): void {
    const config = PERMISSIONS_BY_TID[tid];
    if (config) {
      const { visible } = config;
      this.permissions = visible;
    } else {
      console.warn(`No permissions configured for TID: ${tid}`);
      this.permissions = [];
    }

    this.permissionsSubject.next(this.permissions);
    this.isPermissionsLoaded.next(true);
  }

  /**
   * Devuelve true si el usuario autenticado es Copec (por TID)
   */
  isCopec(): Observable<boolean> {
    return this.authService.getTenantId().pipe(
      map(tid => tid?.toLowerCase() === '79ec8f0d-1881-4537-96ec-570c998ca881')
    );
  }

  /**
   * Devuelve true si el usuario autenticado es OK Mobility (por TID)
   */
  isOkMobility(): Observable<boolean> {
    return this.authService.getTenantId().pipe(
      switchMap((tid) => {
        if (tid?.toLowerCase() === '558b680a-c8dc-4ffe-bd51-5e766efa8f03') {
          return of(true);
        }

        return this.authService.getUserProfile().pipe(
          map((account) => {
            const email = (account?.username ?? '').toLowerCase();
            return email.includes('@okmobility.') || email.includes('@okmobility-');
          })
        );
      })
    );
  }

  /**
   * Extrae un token de oficina para OkMobility a partir del email.
   * Ejemplos: madrid@okmobility.com -> madrid, oficina@okmobility-madrid.com -> madrid
   */
  getOkMobilityOfficeToken(): Observable<string | null> {
    return this.authService.getUserProfile().pipe(
      map((account) => {
        const email = (account?.username ?? '').trim().toLowerCase();
        if (!email.includes('@')) {
          return null;
        }

        const [localPartRaw, domainRaw] = email.split('@');
        const localPart = this.normalizeOfficeToken(localPartRaw);
        const domainNoTld = (domainRaw ?? '').split('.')[0] ?? '';

        if (domainNoTld.startsWith('okmobility-')) {
          const fromDomain = this.normalizeOfficeToken(
            domainNoTld.replace('okmobility-', '')
          );
          return fromDomain || null;
        }

        return localPart || null;
      })
    );
  }

  private normalizeOfficeToken(value: string): string {
    return (value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   *  Reset permissions if no TID found
   */
  private resetPermissions(): void {
    this.permissions = [];
    this.permissionsSubject.next([]);
    this.isPermissionsLoaded.next(false);
  }

  /**
   * Devuelve permisos como observable
   */
  getPermissions(): Observable<Permission[]> {
    return this.permissionsSubject.asObservable();
  }

  /**
   *  Verifica si los permisos están cargados
   */
  arePermissionsLoaded(): Observable<boolean> {
    return this.isPermissionsLoaded.asObservable();
  }

  /**
   *  Verifica si el usuario tiene un permiso específico
   */
  hasPermission(permission: Permission): boolean {
    return this.permissions.includes(permission);
  }

  /**
   *  Verifica si el usuario tiene alguno de los permisos especificados
   */
  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some((permission) =>
      this.permissions.includes(permission)
    );
  }
}
